import { useState, type FormEvent } from "react";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_URL = "https://readdy.ai/api/form/da2m1paamq5jpe1hs0cg";

const contactLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "联系我们 - 洞洞杂货铺世界观解析站",
  description: "联系洞洞杂货铺世界观解析站：提交留言、补充隐藏线索或咨询关于洞洞杂货铺漫画世界观与设定的问题。",
};

const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页", item: "https://storiha.github.io/" },
    { "@type": "ListItem", position: 2, name: "联系我们", item: "https://storiha.github.io/contact" },
  ],
};

type Status = { type: "idle" | "success" | "error"; msg: string };

const inputClass =
  "w-full rounded-xl border border-background-300/70 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-200";

export default function Contact() {
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus({ type: "success", msg: "感谢你的来信，我们已经收到。" });
      form.reset();
      return;
    }
    data.delete("website_alt");

    setSubmitting(true);
    setStatus({ type: "idle", msg: "" });
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      if (res.ok && parsed?.code === "OK") {
        setStatus({ type: "success", msg: "提交成功！感谢你的来信，我们会尽快与你联系。" });
        form.reset();
      } else {
        setStatus({ type: "error", msg: serverMsg || "提交失败，请稍后再试。" });
      }
    } catch {
      setStatus({ type: "error", msg: "网络似乎出了点问题，请稍后再试。" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Seo
        title="联系我们 - 洞洞杂货铺世界观解析站"
        description="想补充洞洞杂货铺漫画的隐藏线索，或对某处世界观设定有疑问？欢迎通过联系我们页面提交留言，我们会尽快与你联系。"
        keywords="洞洞杂货铺联系我们,洞洞杂货铺反馈,洞洞杂货铺隐藏线索投稿"
        canonicalPath="/contact"
        jsonLd={[contactLd, breadcrumbLd]}
      />
      <Navbar />
      <main>
        <div className="mx-auto w-full max-w-6xl px-4 pt-8 md:px-6">
          <Breadcrumb items={[{ label: "联系我们" }]} />
        </div>

        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
            <div>
              <span className="inline-flex rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800 whitespace-nowrap">
                联系我们
              </span>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                和我们聊聊你的发现
              </h1>
              <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
                如果你发现了我们遗漏的隐藏线索，或对某处洞洞杂货铺的设定有自己的独到理解，
                都欢迎通过下面的表单告诉我们。每一份来信，我们都会认真阅读。
              </p>

              <form
                data-readdy-form="洞洞杂货铺联系表单"
                onSubmit={handleSubmit}
                className="mt-8 space-y-5"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="name"
                      className="mb-1.5 block text-sm font-medium text-foreground-900"
                    >
                      你的称呼
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你？"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-1.5 block text-sm font-medium text-foreground-900"
                    >
                      邮箱地址
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于回复你"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="mb-1.5 block text-sm font-medium text-foreground-900"
                  >
                    留言类型
                  </label>
                  <select id="subject" name="subject" className={inputClass} defaultValue="意见反馈">
                    <option value="意见反馈">意见反馈</option>
                    <option value="补充隐藏线索">补充隐藏线索</option>
                    <option value="设定疑问">设定疑问</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="mb-1.5 block text-sm font-medium text-foreground-900"
                  >
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    required
                    maxLength={500}
                    placeholder="写下你想分享的发现或疑问（最多 500 字）"
                    className={`${inputClass} resize-none`}
                  />
                  <p className="mt-1 text-right text-xs text-foreground-600">最多 500 字</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  aria-hidden="true"
                  autoComplete="off"
                  tabIndex={-1}
                  readOnly
                  className="form-fill-status"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      正在提交…
                    </>
                  ) : (
                    <>
                      提交留言
                      <i className="ri-send-plane-line"></i>
                    </>
                  )}
                </button>

                {status.type === "success" && (
                  <p className="rounded-xl bg-accent-100 px-4 py-3 text-sm font-medium text-accent-900">
                    <i className="ri-checkbox-circle-line mr-1.5"></i>
                    {status.msg}
                  </p>
                )}
                {status.type === "error" && (
                  <p className="rounded-xl bg-primary-100 px-4 py-3 text-sm font-medium text-primary-900">
                    <i className="ri-error-warning-line mr-1.5"></i>
                    {status.msg}
                  </p>
                )}
              </form>
            </div>

            <aside className="space-y-5">
              <div className="rounded-2xl bg-background-100/70 p-6 ring-1 ring-background-200/70">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  其他联系方式
                </h2>
                <ul className="mt-4 space-y-4 text-sm text-foreground-700">
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                      <i className="ri-mail-line"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-600">邮箱</p>
                      <p className="mt-0.5">hello@dongdong.example.com</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                      <i className="ri-weibo-line"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-600">微博</p>
                      <p className="mt-0.5">@洞洞杂货铺解析站</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary-100 text-secondary-800">
                      <i className="ri-bilibili-line"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-600">哔哩哔哩</p>
                      <p className="mt-0.5">洞洞杂货铺世界观</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl bg-accent-100/50 p-6 ring-1 ring-accent-200/60">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-500 text-background-50">
                  <i className="ri-lightbulb-line text-lg"></i>
                </span>
                <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  投稿小提示
                </h2>
                <p className="mt-2 text-sm leading-6 text-foreground-700">
                  如果你要补充隐藏线索，建议附上作品的话数或大致出现位置，
                  会大大方便我们核对并收录进设定百科。
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}